import React from 'react';
import { useMemoStore } from '../../stores/memoStore';
import { createMemoStoreActions, memoAsyncActions } from '../../stores/actions/memoActions';
import { memoKeys } from '../api/memoAPI';
import { useMemoListQuery, useMemoDetailQuery } from '../query/memoQuery';
import { useAsyncOperations } from './asyncCommon';
import { useMemoShareManager } from './useMemoShareManager';
import { computeShareStatus } from '../../model/DataConverters';

// Store-based hooks for direct store access
export const useMemoStoreData = () => {
  const memos = useMemoStore((state) => state.memos);
  const currentMemo = useMemoStore((state) => state.currentMemo);
  const currentMemoId = useMemoStore((state) => state.currentMemoId);
  const status = useMemoStore((state) => state.status);
  const editTitle = useMemoStore((state) => state.editTitle);
  const currentViewMemoStatus = useMemoStore((state) => state.currentViewMemoStatus);
  const currentViewMemo = useMemoStore((state) => state.currentViewMemo);
  const searchMemos = useMemoStore((state) => state.searchMemos);
  
  return {
    memos,
    currentMemo,
    currentMemoId,
    status,
    editTitle,
    currentViewMemoStatus,
    currentViewMemo,
    searchMemos
  };
};

// 比较 shareTypes 数组是否浅层相等（元素为基础类型，顺序敏感）
const isSameShareTypes = (a: any[] | undefined, b: any[] | undefined): boolean => {
  const lenA = a?.length ?? 0;
  const lenB = b?.length ?? 0;
  if (lenA !== lenB) return false;
  if (lenA === 0) return true;
  for (let i = 0; i < lenA; i++) {
    if (a![i] !== b![i]) return false;
  }
  return true;
};

// 综合管理hook，结合React Query和Zustand
export const useMemoManager = () => {
  const storeData = useMemoStoreData();
  const { safeAsyncOperation, queryClient } = useAsyncOperations();

  // 获取memo share数据
  const { memoShares } = useMemoShareManager();

  // 使用依赖注入模式创建store actions
  const storeActions = React.useMemo(() => {
    return createMemoStoreActions(useMemoStore.setState, useMemoStore.getState);
  }, []);

  // 创建memo异步actions实例
  const memoActions = React.useMemo(() => {
    return memoAsyncActions(useMemoStore.setState, useMemoStore.getState);
  }, []);

  const {
    data: memosData,
    isLoading: memosLoading,
    error: memosError
  } = useMemoListQuery();

  // 数据合并逻辑：将memo share数据合并到memo数据中
  // 用 Map 索引做 O(n) 查表（原为 map+find 的 O(memos×shares)），
  // 且无变化时复用 store 数组引用，避免 setMemos 触发全量重渲染
  const combinedMemos = React.useMemo(() => {
    if (!storeData.memos.length || !memoShares.length) {
      return storeData.memos;
    }

    const shareMap = new Map(memoShares.map((s: any) => [s.guid, s]));
    let changed = false;

    const result = storeData.memos.map((memo: any) => {
      const share = shareMap.get(memo.guid);
      let hasShare = false;
      let shareTypes: any[] = [];
      if (share && share.shareInfo) {
        ({ hasShare, shareTypes } = computeShareStatus(share.shareInfo));
      }
      // memoShares 已加载但此 memo 无分享记录 → hasShare = false
      // （无分享记录 = 未设置任何共享目标）

      // 已与目标状态一致时复用原对象引用，避免无谓的新对象分配与比较
      if (memo.hasShare === hasShare && isSameShareTypes(memo.shareTypes, shareTypes)) {
        return memo;
      }
      changed = true;
      return { ...memo, hasShare, shareTypes };
    });

    return changed ? result : storeData.memos;
  }, [storeData.memos, memoShares]);

  // 将合并后的数据同步到Store（仅在引用变化时写回，取代原来的两次全量 JSON.stringify 比较）
  React.useEffect(() => {
    if (combinedMemos !== storeData.memos) {
      storeActions.setMemos(combinedMemos);
    }
  }, [combinedMemos, storeData.memos]);
  
  const { 
    data: currentMemoData, 
    isLoading: currentMemoLoading, 
    error: currentMemoError 
  } = useMemoDetailQuery(storeData.currentMemoId || '', { enabled: false });
  
  // 数据同步逻辑：将React Query数据同步到Zustand store
  React.useEffect(() => {
    if (memosData && !memosLoading) {
      // 只有当store中无数据时才用React Query数据初始化store，避免覆盖乐观更新
      const currentStoreData = useMemoStore.getState().memos;
      if (currentStoreData.length === 0) {
        storeActions.setMemos(memosData);
        storeActions.setStatus('idle');
      }
    }
  }, [memosData, memosLoading]);
  
  // 当前memo数据同步
  React.useEffect(() => {
    if (currentMemoData && !currentMemoLoading && storeData.currentMemoId) {
      const currentStoreData = useMemoStore.getState().currentMemo;
      if (!currentStoreData || currentStoreData.guid !== storeData.currentMemoId) {
        storeActions.setCurrentViewMemo(currentMemoData.content);
      }
    }
  }, [currentMemoData, currentMemoLoading, storeData.currentMemoId]);
  
  // 智能错误处理
  const hasError = memosError || currentMemoError;
  
  const createMemoSync = async (memo: any) => {
    const result = await safeAsyncOperation(async () => {
      // 1. 乐观更新：先添加到本地状态
      storeActions.addMemo(memo);
      storeActions.setSelectedMemoId(memo.guid);
      
      // 2. 同步到远程服务器（使用正确的参数格式）
      await memoActions.updateMemoAsync({ memo });
      
      // 3. 更新React Query缓存
      const listQueryKey = memoKeys.list();
      const currentMemos = queryClient.getQueryData<any[]>(listQueryKey) || [];
      
      if (!currentMemos.some(m => m.guid === memo.guid)) {
        const updatedMemos = [...currentMemos, memo];
        queryClient.setQueryData(listQueryKey, updatedMemos);
      }
      
      // 4. 更新单个memo的缓存
      queryClient.setQueryData(memoKeys.memo(memo.guid), memo);
      
      return { success: true, memo };
    }, '创建memo失败');
    
    return result.success;
  };

  const forceRefreshMemoData = async (memoId?: string) => {
    const result = await safeAsyncOperation(async () => {
      // 如果指定了memoId，强制获取该memo的数据，无论本地是否存在
      if (memoId) {
        // 使用fetchQuery而不是refetchQueries，确保即使本地没有缓存也会强制从服务器获取
        await queryClient.fetchQuery({ 
          queryKey: memoKeys.memo(memoId),
          queryFn: () => memoActions.fetchMemoAsync(memoId)
        });
      } else {
        // 如果没有指定memoId，保持原有行为（刷新当前选中的memo）
        if (storeData.currentMemoId) {
          await queryClient.refetchQueries({ queryKey: memoKeys.memo(storeData.currentMemoId) });
        }
      }
      return { success: true };
    }, '强制刷新memo数据失败');
    
    return result.success;
  };
  
  const syncAllMemoData = async () => {
    const result = await safeAsyncOperation(async () => {
      await queryClient.refetchQueries({ queryKey: memoKeys.list() });
      if (storeData.currentMemoId) {
        await queryClient.refetchQueries({ queryKey: memoKeys.memo(storeData.currentMemoId) });
      }
      return { success: true };
    }, '同步所有memo数据失败');
    
    return result.success;
  };
  
  return {
    // Store状态 - 返回合并后的数据
    memos: combinedMemos,
    currentMemo: storeData.currentMemo,
    currentMemoId: storeData.currentMemoId,
    status: storeData.status,
    editTitle: storeData.editTitle,
    currentViewMemoStatus: storeData.currentViewMemoStatus,
    currentViewMemo: storeData.currentViewMemo,
    searchMemos: storeData.searchMemos,
    
    // Loading states
    isLoading: memosLoading || currentMemoLoading,
    memosLoading,
    currentMemoLoading,
    
    // Error states
    error: hasError,
    memosError,
    currentMemoError,
    
    // Store操作
    storeActions,
    
    // Integration actions
    createMemoSync,
    forceRefresh: forceRefreshMemoData,
    syncAllData: syncAllMemoData,
    
    // 异步方法（直接暴露给组件使用）
    updateMemoAsync: memoActions.updateMemoAsync,
    updateMemoStatusByIdAsync: memoActions.updateMemoStatusByIdAsync,
    transferToDefaultByGroupId: memoActions.transferToDefaultByGroupId,
    
    // 同步方法（直接暴露给组件使用）
    setSelectedMemoId: storeActions.setSelectedMemoId,
    addMemo: storeActions.addMemo,
    setEditTitle: storeActions.setEditTitle,
    
    // Combined state
    hasMemos: combinedMemos.length > 0,
    isMemoSelected: !!storeData.currentMemoId,
    isMemoLoading: memosLoading || currentMemoLoading || storeData.status === 'loading',
    
    // 智能状态判断
    canCreateMemo: !memosLoading && !hasError,
    canEditMemo: !!storeData.currentMemoId && !currentMemoLoading && !hasError,
    canDeleteMemo: !!storeData.currentMemoId && !hasError,
    
    // 数据同步状态
    isDataSynced: () => {
      const storeMemos = combinedMemos;
      const queryMemos = memosData || [];
      return !memosData || (storeMemos.length === queryMemos.length &&
        storeMemos.every((memo, index) => memo.guid === queryMemos[index]?.guid));
    }
  };
};